"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Stethoscope } from "lucide-react";
import posthog from "posthog-js";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function RegisterPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onRegister(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (!consent) {
      setError("Você precisa aceitar o consentimento de tratamento de dados.");
      posthog.capture("register_error", { reason: "consent_not_accepted" });
      return;
    }

    setLoading(true);
    posthog.capture("register_submitted", { has_consent: true });

    const supabase = createClient();

    // Read the referral cookie if present
    const getCookie = (name: string) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(';').shift();
      return undefined;
    };
    let referralCode = getCookie("medscan_ref");
    if (referralCode) {
      try {
        referralCode = decodeURIComponent(referralCode);
        const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        if (!uuidRegex.test(referralCode)) {
          referralCode = undefined;
        }
      } catch (e) {
        referralCode = undefined;
      }
    }

    const { error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback?next=/scan`,
        data: referralCode ? { referred_by: referralCode } : undefined,
      },
    });

    setLoading(false);

    if (authError) {
      setError(authError.message);
      posthog.capture("register_error", { error: authError.message });
      return;
    }

    posthog.capture("register_success");
    setMessage("Conta criada. Verifique seu e-mail para confirmação, se habilitado no Supabase.");
    router.push("/login");
  }

  return (
    <div className="flex h-screen w-full overflow-hidden" style={{ background: "var(--background)" }}>
      {/* Left — decorative panel */}
      <div
        className="relative hidden lg:flex lg:w-1/2 flex-col justify-between p-10 overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0c4a6e 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.08) 1px,transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative flex items-center gap-2.5 z-10">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: "var(--accent)" }}>
            <Stethoscope size={18} className="text-white" />
          </div>
          <span className="text-lg font-semibold text-white">MedScan Extract</span>
        </div>
        <div className="relative z-10">
          <h2 className="text-3xl font-bold text-white leading-snug">
            Comece a extrair
            <br />
            dados em minutos
          </h2>
          <p className="mt-3 text-base text-blue-200/80">
            Crie sua conta e automatize a extração de procedimentos cirúrgicos com IA.
          </p>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex w-full lg:w-1/2 flex-col items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex items-center gap-2.5 lg:hidden">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: "var(--accent)" }}>
              <Stethoscope size={15} className="text-white" />
            </div>
            <span className="text-sm font-semibold" style={{ color: "var(--text)" }}>MedScan Extract</span>
          </div>

          <div>
            <h1 className="text-2xl font-bold" style={{ color: "var(--text)" }}>Criar conta</h1>
            <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
              Cadastre-se para usar o MedScan Extract.
            </p>
          </div>

          <form onSubmit={onRegister} className="space-y-4">
            <Input
              id="email"
              label="E-mail"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="medico@hospital.com"
            />
            <Input
              id="password"
              label="Senha"
              type="password"
              minLength={6}
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
            />

            <label className="flex items-start gap-2.5 cursor-pointer group">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-zinc-600 accent-blue-600"
              />
              <span className="text-sm leading-snug" style={{ color: "var(--text-muted)" }}>
                Concordo com o tratamento de dados sensíveis para extração e histórico.
              </span>
            </label>

            {error && (
              <p className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-400">
                {error}
              </p>
            )}
            {message && (
              <p className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-400">
                {message}
              </p>
            )}

            <Button type="submit" size="lg" disabled={loading} className="w-full">
              {loading ? "Criando..." : "Criar conta"}
            </Button>
          </form>

          <p className="text-center text-sm" style={{ color: "var(--text-muted)" }}>
            Já possui conta?{" "}
            <Link href="/login" className="font-medium hover:text-zinc-100 transition-colors" style={{ color: "var(--text)" }}>
              Entrar
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
